import { Blueprint } from '../../blueprint.class';
import { BlueprintInstanceType } from '../../../shared.enums';
import { PanelItem } from '../../../panel-items/panel-item.class';
import { Vertex3 } from '../../../../spatial-classes';
import { ToolResult } from '../../types';

export interface MoveApplianceGroupArgs {
	applianceId: string;
	referenceApplianceId?: string;
	position: 'left' | 'right';
}

export const moveApplianceGroupTool = (blueprint: Blueprint, { applianceId, referenceApplianceId, position }: MoveApplianceGroupArgs): ToolResult => {
	if (!applianceId) return { success: false, error: 'Missing applianceId. Use list_appliances to get valid item IDs.' };
	if (!position || (position !== 'left' && position !== 'right')) {
		return { success: false, error: "Missing or invalid position. Must be 'left' or 'right'." };
	}

	const kitchen = blueprint.kitchens.getActiveVersion();
	if (!kitchen) return { success: false, error: 'No active kitchen found.' };

	// Find the appliance to move
	const [type, item] = kitchen.getReferenceItem(applianceId);
	if (!item) return { success: false, error: `Appliance '${applianceId}' not found. Use list_appliances to get valid item IDs.` };

	if (type !== BlueprintInstanceType.PanelItem) {
		return { success: false, error: `Item '${applianceId}' is not an appliance (type: ${type}).` };
	}

	const panelItem = item as PanelItem;
	const group = panelItem.refMap.panelItemGroup;
	const panel = panelItem.refMap.panel;

	if (!group) return { success: false, error: `Could not find appliance group for '${applianceId}'.` };
	if (!panel) return { success: false, error: `Could not find panel for '${applianceId}'.` };

	if (referenceApplianceId) {
		// Position relative to a reference appliance's group
		const [refType, refItem] = kitchen.getReferenceItem(referenceApplianceId);
		if (!refItem) return { success: false, error: `Reference appliance '${referenceApplianceId}' not found. Use list_appliances to get valid item IDs.` };

		if (refType !== BlueprintInstanceType.PanelItem) {
			return { success: false, error: `Reference item '${referenceApplianceId}' is not an appliance (type: ${refType}).` };
		}

		const refPanelItem = refItem as PanelItem;
		const refGroup = refPanelItem.refMap.panelItemGroup;
		const refPanel = refPanelItem.refMap.panel;

		if (!refGroup) return { success: false, error: `Could not find appliance group for reference '${referenceApplianceId}'.` };

		if (refGroup.id === group.id) {
			return { success: false, error: 'The appliance and reference appliance are in the same group. Use a reference from a different group.' };
		}

		if (refPanel?.id !== panel.id) {
			return { success: false, error: 'The appliance and reference appliance must be on the same panel.' };
		}

		// Set x position relative to the reference group
		const offset = position === 'left' ? -1 : 1;
		group.center = new Vertex3({ dataIn: { x: refGroup.center.x + offset } });
	} else {
		// Position relative to island edge
		const halfWidth = panel.size.width / 2;
		const x = position === 'left' ? -halfWidth + 1 : halfWidth - 1;
		group.center = new Vertex3({ dataIn: { x } });
	}

	// A reorder re-spaces the whole run: hand-placed positions on the panel are forgotten
	panel.resetFineTune();
	// Distribute horizontally to properly space all groups
	panel.distributeApplianceGroupsHorizontally();

	const positionDesc = referenceApplianceId
		? `${position} of reference appliance '${referenceApplianceId}'`
		: `${position} side of the island`;

	return {
		success: true,
		message: `Moved appliance group to the ${positionDesc}.`
	};
};
